import React from "react";
import {
  Box,
  ButtonBase,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  Chip,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DoneAllTwoToneIcon from "@mui/icons-material/DoneAllTwoTone";
import AddIcon from "@mui/icons-material/Add";
import VideoPlayer from "./VideoPlayer";

/**
 * Video Card Component for MRSS Management
 * Displays video information with add/remove functionality
 */
function VideoCard({
  video,
  handleAdd = undefined,
  onDelete = undefined,
  _component = ButtonBase,
  sx,
  selected = false,
}) {
  return (
    <Box
      component={_component}
      key={video.unique_key}
      sx={{
        border: selected ? "2px solid" : "0.5px solid #d3d3d3",
        borderColor: selected ? "primary.main" : "#d3d3d3",
        ...sx,
      }}
      display="flex"
      flexDirection="column"
      borderRadius={2}
      gap={1}
      p={1}
      width="100%"
      minWidth={0}
      boxSizing="border-box"
    >
      {/* Action buttons */}
      {(onDelete || selected) && (
        <Box
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          {onDelete ? (
            <IconButton size="small" onClick={() => onDelete(video)}>
              <ClearIcon fontSize="small" />
            </IconButton>
          ) : (
            <Box />
          )}
          {selected && (
            <Chip
              icon={<DoneAllTwoToneIcon fontSize="small" />}
              label="Selected"
              color="success"
              size="small"
            />
          )}
        </Box>
      )}

      {/* Video Preview */}
      <Box
        textAlign="center"
        width={"100%"}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "#000",
          height: { xs: "auto", sm: 200 },
          aspectRatio: { xs: "16 / 9", sm: "auto" },
          "& [data-vjs-player], & .video-js": { width: "100% !important", height: "100% !important" },
        }}
      >
        <VideoPlayer src={video.s3_url} />
      </Box>

      {/* Video Info */}
      <Box display="flex" flexDirection="column" width="100%" minHeight={60}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" columnGap={1}>
          <Typography variant="subtitle2" color="gray" align="left" sx={{ minWidth: 0, wordBreak: "break-all" }}>
            {video.unique_key}
          </Typography>
          {video.created_at && (
            <Typography variant="subtitle2" color="gray">
              {new Date(video.created_at).toDateString()}
            </Typography>
          )}
        </Box>
        <Divider flexItem variant="middle" />
        {video.description ? (
          <Tooltip title={video.description}>
            <Typography variant="body2" align="left" sx={{ overflowWrap: "anywhere" }}>
              {video.title ? video.title : video.video_title}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" align="left" sx={{ overflowWrap: "anywhere" }}>
            {video.title ? video.title : video.video_title}
          </Typography>
        )}

        {/* Add button for non-selected videos */}
        {handleAdd && !selected && (
          <Box mt={1}>
            <Chip
              icon={<AddIcon />}
              label="Add to selection"
              color="primary"
              variant="outlined"
              size="medium"
              sx={{ width: "100%" }}
              onClick={handleAdd(video)}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default VideoCard;
